import type { MascotMood, MascotView } from '../../game/gameView';

const SVG_NS = 'http://www.w3.org/2000/svg';

export interface Mascot extends MascotView {
  readonly element: HTMLElement;
  destroy(): void;
}

interface MoodPieces {
  smile?: SVGPathElement;
  oMouth?: SVGEllipseElement;
}

export function createMascot(sizeRem = 12.5): Mascot {
  const wrap = document.createElement('div');
  wrap.className = 'mascot';
  wrap.style.width = `${sizeRem}rem`;
  wrap.style.height = `${sizeRem}rem`;

  const root = document.createElementNS(SVG_NS, 'svg');
  root.setAttribute('viewBox', '0 0 200 200');
  root.style.width = `${sizeRem}rem`;
  root.style.height = `${sizeRem}rem`;

  function s(name: string, attrs: Record<string, string>): SVGElement {
    const el = document.createElementNS(SVG_NS, name);
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
    return el;
  }

  // tufts
  root.appendChild(s('ellipse', { cx: '78', cy: '48', rx: '14', ry: '18', fill: '#7CCFB8', stroke: '#2A2154', 'stroke-width': '5' }));
  root.appendChild(s('ellipse', { cx: '122', cy: '48', rx: '14', ry: '18', fill: '#7CCFB8', stroke: '#2A2154', 'stroke-width': '5' }));
  // body
  root.appendChild(s('ellipse', { cx: '100', cy: '115', rx: '74', ry: '68', fill: '#9FE2CD', stroke: '#2A2154', 'stroke-width': '5' }));
  // belly
  root.appendChild(s('ellipse', { cx: '100', cy: '135', rx: '44', ry: '32', fill: '#C8F0E0' }));
  // cheeks
  root.appendChild(s('circle', { cx: '62', cy: '128', r: '11', fill: '#FFB59E', opacity: '0.7' }));
  root.appendChild(s('circle', { cx: '138', cy: '128', r: '11', fill: '#FFB59E', opacity: '0.7' }));
  // eyes
  const eyes = s('g', { class: 'mascot-eyes' });
  eyes.appendChild(s('circle', { cx: '78', cy: '105', r: '11', fill: '#2A2154' }));
  eyes.appendChild(s('circle', { cx: '122', cy: '105', r: '11', fill: '#2A2154' }));
  eyes.appendChild(s('circle', { cx: '81', cy: '101', r: '4', fill: 'white' }));
  eyes.appendChild(s('circle', { cx: '125', cy: '101', r: '4', fill: 'white' }));
  root.appendChild(eyes);
  // arms
  root.appendChild(s('ellipse', { cx: '32', cy: '130', rx: '10', ry: '14', fill: '#9FE2CD', stroke: '#2A2154', 'stroke-width': '4' }));
  root.appendChild(s('ellipse', { cx: '168', cy: '130', rx: '10', ry: '14', fill: '#9FE2CD', stroke: '#2A2154', 'stroke-width': '4' }));

  const pieces: MoodPieces = {};

  function clearMouth(): void {
    if (pieces.smile) {
      pieces.smile.remove();
      delete pieces.smile;
    }
    if (pieces.oMouth) {
      pieces.oMouth.remove();
      delete pieces.oMouth;
    }
  }

  function applyMood(mood: MascotMood): void {
    clearMouth();
    if (mood === 'cheer') {
      const path = s('path', {
        d: 'M 75 132 Q 100 162 125 132 Z',
        fill: '#D63672',
        stroke: '#2A2154',
        'stroke-width': '4',
      }) as SVGPathElement;
      root.appendChild(path);
      pieces.smile = path;
    } else if (mood === 'happy') {
      const path = s('path', {
        d: 'M 80 132 Q 100 150 120 132',
        fill: 'none',
        stroke: '#2A2154',
        'stroke-width': '5',
        'stroke-linecap': 'round',
      }) as SVGPathElement;
      root.appendChild(path);
      pieces.smile = path;
    } else if (mood === 'sad') {
      const path = s('path', {
        d: 'M 80 148 Q 100 132 120 148',
        fill: 'none',
        stroke: '#2A2154',
        'stroke-width': '5',
        'stroke-linecap': 'round',
      }) as SVGPathElement;
      root.appendChild(path);
      pieces.smile = path;
    } else {
      const oh = s('ellipse', { cx: '100', cy: '140', rx: '9', ry: '12', fill: '#2A2154' }) as SVGEllipseElement;
      root.appendChild(oh);
      pieces.oMouth = oh;
    }
  }

  applyMood('happy');
  wrap.appendChild(root);

  wrap.addEventListener('click', () => {
    if (wrap.classList.contains('blinking')) return;
    wrap.classList.add('blinking');
    eyes.addEventListener(
      'animationend',
      () => {
        wrap.classList.remove('blinking');
      },
      { once: true },
    );
  });

  function setMood(mood: MascotMood): void {
    applyMood(mood);
  }

  function destroy(): void {
    wrap.remove();
  }

  return { element: wrap, setMood, destroy };
}
